import { Component, Input, Output, EventEmitter, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import { BalanceAccountService } from 'src/app/share/services/balance-account/balance-account.service';
import { PaymentAccountsService } from 'src/app/share/services/payment-account/payment-accounts.service';
import { StorageService } from '../services/storage-service/storage.service';
import { StorageKeys } from '../enums/storage.enum';

@Component({
  selector: 'app-new-payment-account',
  templateUrl: './new-payment-account.component.html',
  styleUrls: ['./new-payment-account.component.scss'],
  standalone: false
})
export class NewPaymentAccountComponent implements OnInit {

  @Input() mode: 'ADD' | 'VIEW' | 'EDIT' = 'ADD';
  @Input() showBranch: boolean = true;
  @Input() preSelectedBranch: string = '';
  @Input() preSelectedMode: string = '';
  @Input() branches: any[] = [];
  @Input() editAccountData: any = null;

  @Output() accountSave = new EventEmitter<any>();
  @Output() accountCancel = new EventEmitter<void>();

  accountForm!: FormGroup;
  balanceAccounts: any[] = [];
  faSpinner: any = faSpinner;
  loader: any = false;
  errorMessage: string = '';
  isBranchVisible: boolean = false;

  paymentTypes = [
    { label: 'Cash', key: 'Cash' },
    { label: 'UPI', key: 'UPI' },
    { label: 'Bank Transfer', key: 'BankTransfer' },
    { label: 'Card', key: 'Card' },
    { label: 'Bajaj Finance', key: 'BajajFinance'}
  ];

  constructor(
    private readonly fb: FormBuilder,
    private readonly balanceAccountService: BalanceAccountService,
    private readonly paymentAccountsService: PaymentAccountsService,
    private readonly storageService: StorageService,
  ) { }

  ngOnInit() {
    let userDetailsData = this.storageService.getDataFromLocalStorage(StorageKeys.SELF_DETAILS);
    this.isBranchVisible = userDetailsData?.enrollmentType === 'Company';
    if (this.isBranchVisible) {
      this.getBalanceAccounts();
    }
    this.initForm();
  }

  getBalanceAccounts() {
    this.balanceAccountService.getBalanceAccounts({}).subscribe({
      next: (res: any) => {
        this.balanceAccounts = res.data || [];
      },
      error: () => {
        this.balanceAccounts = [];
      }
    });
  }

  private initForm() {
    this.accountForm = this.fb.group({
      company: [null, Validators.required],
      paymentType: [null, Validators.required],
      accountName: ['', [Validators.required, Validators.maxLength(50)]],
      settlementType: ['Manual', Validators.required],
      balanceAccountId: [null],
      settlementPeriod: [null],
      status: [true]
    });

    if (this.preSelectedBranch) {
      this.accountForm.patchValue({ company: this.preSelectedBranch });
    }

    if (this.preSelectedMode) {
      this.accountForm.patchValue({ paymentType: this.preSelectedMode });
    }

    if (this.mode === 'VIEW' && this.editAccountData) {
      const data = this.editAccountData;
      const typeVal = ['Immediate', 'Auto', 'Manual'].includes(data.settlementType) ? data.settlementType : null

      this.accountForm.patchValue({
        accountName: data.accountName,
        paymentType: data.paymentType,
        company: data.company?._id || null,
        settlementType: typeVal,
        status: data.status === undefined ? true : data.status,
        balanceAccountId: data.balanceAccountId?._id || null,
        settlementPeriod: data.settlementPeriod || null
      });

      this.onSettlementTypeChange(typeVal);
      this.accountForm.disable();
    } else {
      this.onSettlementTypeChange('Manual');
    }
  }

  onAccountNameInput(event: any): void {
    const input = event.target;
    const start = input.selectionStart;
    const end = input.selectionEnd;
    const upper = (input.value || '').toUpperCase();
    this.accountForm.get('accountName')?.setValue(upper, { emitEvent: false });
    setTimeout(() => {
      input.setSelectionRange(start, end);
    });
  }

  onSettlementTypeChange(type: 'Immediate' | 'Auto' | 'Manual'): void {
    if (this.accountForm.get('settlementType')?.disabled) return;
    this.accountForm.get('settlementType')?.setValue(type);

    const balanceAccCtrl = this.accountForm.get('balanceAccountId');
    const periodCtrl = this.accountForm.get('settlementPeriod');

    if (type === 'Immediate') {
      balanceAccCtrl?.setValidators([Validators.required]);
      periodCtrl?.clearValidators();
      periodCtrl?.setValue(null);
    } else if (type === 'Auto') {
      balanceAccCtrl?.setValidators([Validators.required]);
      periodCtrl?.setValidators([Validators.required, Validators.min(1), Validators.max(7)]);
    } else {
      balanceAccCtrl?.clearValidators();
      balanceAccCtrl?.setValue(null);
      periodCtrl?.clearValidators();
      periodCtrl?.setValue(null);
    }

    balanceAccCtrl?.updateValueAndValidity();
    periodCtrl?.updateValueAndValidity();
  }

  switchToEdit(): void {
    this.mode = 'EDIT';
    this.accountForm.enable();
    this.accountForm.get('company')?.disable();
    if (this.editAccountData?.default) {
      this.accountForm.get('paymentType')?.disable();
      this.accountForm.get('settlementType')?.disable();
      this.accountForm.get('balanceAccountId')?.disable();
      this.accountForm.get('settlementPeriod')?.disable();
      this.accountForm.get('status')?.disable();
    }
  }

  save() {
    if (this.accountForm.invalid) return;

    this.loader = true;
    this.errorMessage = '';

    const rawValues = this.accountForm.getRawValue();
    const payload: any = {
      accountName: (rawValues.accountName || '').trim().toUpperCase(),
      paymentType: rawValues.paymentType,
      company: rawValues.company,
      settlementType: rawValues.settlementType,
      status: rawValues.status
    };

    if (rawValues.settlementType === 'Auto') {
      payload.settlementPeriod = Number(rawValues.settlementPeriod);
      payload.balanceAccountId = rawValues.balanceAccountId;
    } else if (rawValues.settlementType === 'Immediate') {
      payload.balanceAccountId = rawValues.balanceAccountId;
    }

    if (this.mode === 'ADD') {
      delete payload.status; 
      this.paymentAccountsService.createNewPaymentaccount(payload).subscribe({
        next: (res: any) => {
          this.loader = false;
          this.accountSave.emit(payload);
        },
        error: (error: any) => {
          this.loader = false;
          this.errorMessage = error?.error?.error?.message || error?.error?.message || 'Something went wrong';
        }
      });
    } else {
      this.paymentAccountsService.editPaymentAccount(this.editAccountData._id, payload).subscribe({
        next: (res: any) => {
          this.loader = false;
          this.accountSave.emit( payload);
        },
        error: (error: any) => {
          this.loader = false;
          this.errorMessage = error?.error?.error?.message || error?.error?.message || 'Something went wrong';
        }
      });
    }
  }

  cancel() {
    this.accountCancel.emit();
  }
}
